import { ArrowRightIcon, CheckCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '../../hooks';
import { analytics } from '../../index';
import { bookingApi } from '../../services/crm/booking_public';
import { createAccountPublic } from '../../services/login/signup_public';
import { TOS_VERSION } from '../login/TermsOfService.component';
import { isCrmError } from '../../services/crm';
import { BookingShell, InlineError, ShellOptions, Steps } from '../book/components/BookingShell.component';
import { defaultNewsConsent, NewsOptIn } from '../book/components/NewsOptIn.component';
import { useEmbed } from '../book/useEmbed';
import { AccountOutcome, AccountStep } from './AccountStep.component';
import {
    attributionFrom, DemoNext, Door, doorOrder, EMPTY_VALUES, errorMessage, GetStartedValues, newSubmissionId, nextPath,
    payloadFor, PERSONAS, PLATFORMS, PRODUCTS, UUID, validateStep, VOLUMES
} from './get_started_rules';

const DRAFT_KEY = 'wallmates.get-started.v1';
const SIGNUP_DRAFT_KEY = 'wallmates.new-account.v1';
const imagesBucket = process.env.REACT_APP_AWS_BUCKET_IMAGES || '';
const BACKDROP = `https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Log-In.png`;

interface Draft {
    values: GetStartedValues;
    step: 0 | 1 | 2;
    /** The request already sent for a door, so a retry (or coming back) reuses it instead of making another. */
    sent: { door: Door; id: string } | null;
}

const readDraft = (key: string): Draft => {
    try {
        const d = JSON.parse(window.sessionStorage.getItem(key) ?? 'null');
        if (d && typeof d === 'object' && d.values) return { values: { ...EMPTY_VALUES, ...d.values }, step: [0, 1, 2].includes(d.step) ? d.step : 0, sent: d.sent && UUID.test(d.sent.id) ? d.sent : null };
    } catch { /* storage blocked or bad JSON: start fresh */ }
    return { values: { ...EMPTY_VALUES, marketing_consent: defaultNewsConsent() }, step: 0, sent: null };
};

const DOORS: Record<Door, { title: string; copy: string; cta: string }> = {
    signup: { title: 'Create your account', copy: 'Set up your brand, upload your designs and see them as real products.', cta: 'Takes about a minute' },
    book: { title: 'Book a call', copy: 'Talk through your plans with a Wallmates specialist first. 15 minutes, on video.', cta: 'Pick a time' }
};

const inputCls = (bad: boolean) => `h-11 w-full rounded-xl border bg-surface px-3 text-sm outline-none transition focus:ring-2 focus:ring-emerald-600 ${bad ? 'border-red-400' : 'border-surface-border'}`;

// #region GetStarted
/**
 * The website's "Get started" form as a widget: what you do, about you, then create an account or book a
 * call. It sends the CRM the same request as the new marketing site. The account door opens in place for a
 * password and the terms, then creates the account; the call door continues to the team calendar.
 * Embedded with ?embed=1 by /embed/get-started.js. With page="signup" it is the portal's own sign-up page
 * (/new-account): same steps, the account comes first and the call is the smaller option.
 */
const GetStarted: React.FunctionComponent<{ page?: 'signup' }> = ({ page }): React.ReactElement => {
    const [params] = useSearchParams();
    const embedded = useEmbed(params);
    const signupPage = page === 'signup';
    const draftKey = signupPage ? SIGNUP_DRAFT_KEY : DRAFT_KEY;
    const attribution = React.useMemo(() => {
        const a = attributionFrom(params);
        return signupPage ? { ...a, entry_point: (params.get('src') || 'portal_signup').slice(0, 100) } : a;
    }, [params, signupPage]);
    const [draft, setDraft] = React.useState<Draft>(() => readDraft(draftKey));
    const [tried, setTried] = React.useState(false);
    const [busy, setBusy] = React.useState<Door | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [accountOpen, setAccountOpen] = React.useState(false);
    const [created, setCreated] = React.useState<{ email: string; company: string } | null>(null);
    const [signupOn, setSignupOn] = React.useState(true);
    const [FeatureFlags] = useFeatureFlags();
    const v = draft.values;

    // Sign-ups switched off: only the call door is offered (the sign-up page goes back to sign in, as before).
    React.useEffect(() => {
        FeatureFlags.get('show-create-account').then((on: boolean) => {
            if (on) return;
            if (signupPage) { window.location.replace('/login'); return; }
            setSignupOn(false); setAccountOpen(false);
        });
    }, []);

    React.useEffect(() => {
        try { window.sessionStorage.setItem(draftKey, JSON.stringify(draft)); } catch { /* private mode: no draft, still works */ }
    }, [draft]);

    const set = (patch: Partial<GetStartedValues>) => setDraft((d) => ({ ...d, values: { ...d.values, ...patch } }));
    const toggle = <K extends 'personas' | 'product_interests'>(key: K, value: GetStartedValues[K][number]) =>
        set({ [key]: (v[key] as string[]).includes(value) ? (v[key] as string[]).filter((x) => x !== value) : [...(v[key] as string[]), value] } as Partial<GetStartedValues>);

    const errors = draft.step < 2 ? validateStep(draft.step as 0 | 1, v) : {};
    const show = (key: keyof GetStartedValues) => (tried ? errors[key] : undefined);

    const next = () => {
        setTried(true);
        if (Object.keys(errors).length) return;
        setTried(false); setError(null);
        setDraft((d) => ({ ...d, step: Math.min(2, d.step + 1) as 0 | 1 | 2 }));
    };
    const back = () => {
        setError(null);
        if (accountOpen) { setAccountOpen(false); return; }
        setDraft((d) => ({ ...d, step: Math.max(0, d.step - 1) as 0 | 1 | 2 }));
    };

    /** Saves their answers to the CRM for a door. Same door again (a retry): the same id, so nothing is saved twice. */
    const send = async (door: Door): Promise<{ id: string; next?: DemoNext } | { error: string }> => {
        const id = draft.sent?.door === door ? draft.sent.id : newSubmissionId();
        let payload: Record<string, unknown>;
        try { payload = payloadFor(v, id, attribution, door); } catch (err) { return { error: (err as Error).message }; }
        const result = await bookingApi.getStarted(payload);
        if (isCrmError(result)) return { error: errorMessage(result.status) };
        setDraft((d) => ({ ...d, sent: { door, id } }));
        // The host page hears which door was taken; never who they are.
        if (embedded) window.parent.postMessage({ type: 'wallmates:get_started', door }, '*');
        return { id, next: result.next };
    };

    const book = async () => {
        setBusy('book'); setError(null);
        const sent = await send('book');
        if ('error' in sent) { setBusy(null); setError(sent.error); return; }
        window.location.assign(nextPath({ kind: sent.next?.kind ?? 'book', team_slug: sent.next?.team_slug }, sent.id, v, embedded));
    };

    // One click: save their answers, then create the account with the same request id. Sign-up itself is unchanged.
    const createAccount = async (password: string, skipDuplicateCheck: boolean, honeypot: string): Promise<AccountOutcome> => {
        // Bot trap filled: don't save a CRM lead either; the backend fakes the sign-up.
        const sent = honeypot ? { id: newSubmissionId() } : await send('signup');
        if ('error' in sent) return { ok: false, reason: 'failed', message: sent.error };
        const result = await createAccountPublic({
            first: v.first_name.trim(), last: v.last_name.trim(), email: v.email.trim(), company: v.company_name.trim(),
            // AccountStep only calls onCreate once the terms box is ticked.
            password, submissionId: sent.id, skipDuplicateCheck, tosVersion: TOS_VERSION, honeypot
        });
        if (!result.ok) return result;
        if (!honeypot) analytics.track('SIGNUP');
        // Only the request id leaves the frame, never the name or email.
        if (embedded) window.parent.postMessage({ type: 'wallmates:signed_up', submission_id: sent.id }, '*');
        setCreated({ email: v.email.trim(), company: v.company_name.trim() });
        try { window.sessionStorage.removeItem(draftKey); } catch { /* nothing saved */ }
        return { ok: true };
    };

    const order = doorOrder(v).filter((door) => signupOn || door !== 'signup');
    // On the sign-up page the account step follows straight on; the call is a link under it.
    const showAccount = draft.step === 2 && signupOn && (accountOpen || signupPage);
    const shell: ShellOptions = signupPage ? {
        backdrop: BACKDROP, narrow: true, footer: null,
        label: <>Already have an account? <a href="/login" className="font-semibold underline underline-offset-2">Sign in</a></>
    } : {};

    if (created) return (
        <BookingShell {...shell}>
            <div className="flex flex-col items-center gap-y-3 p-6 text-center sm:p-10">
                <CheckCircleIcon className="h-11 w-11 text-emerald-600" strokeWidth={1.5} />
                <h2 className="text-xl font-semibold">Check your inbox</h2>
                <p className="max-w-sm text-sm text-zinc-600 dark:text-zinc-400">We sent a verification link to <span className="font-medium text-zinc-900 dark:text-zinc-100">{created.email}</span>. Open it to finish setting up {created.company}.</p>
                {signupPage
                    ? <a href="/login" className="mt-2 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">Go to sign in</a>
                    : <a href="/login" target="_blank" rel="noopener" className="mt-2 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">Open Wallmates</a>}
            </div>
        </BookingShell>
    );

    return (
        <BookingShell {...shell}>
            <div className="flex flex-col gap-y-5 p-5 sm:p-7">
                <div className="flex flex-col gap-y-3">
                    <Steps current={draft.step + 1} labels={['What you do', 'About you', signupPage ? 'Your account' : 'Your next step']} />
                    <div>
                        <h2 className="text-xl font-semibold tracking-tight">
                            {draft.step === 0 ? 'What are you building?' : draft.step === 1 ? 'Tell us about you' : showAccount ? 'Create your account' : 'How would you like to start?'}
                        </h2>
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                            {draft.step === 0 ? 'Pick what fits.' : draft.step === 1 ? 'So we can set things up for you.' : showAccount ? 'Last step. You’ll confirm your email next, then you’re in.' : 'You can always do the other one later.'}
                        </p>
                    </div>
                </div>

                {draft.step === 0 ? (
                    <div className="flex flex-col gap-y-5">
                        <ChoiceGroup title="Which roles describe you?" help="Select all that apply. Your first choice sets up your account."
                            options={PERSONAS} picked={v.personas} onToggle={(p) => toggle('personas', p)} error={show('personas')} />
                        <ChoiceGroup title="What would you like to create or sell?" help="Select any that interest you, or leave this open if you’re exploring."
                            options={PRODUCTS} picked={v.product_interests} onToggle={(p) => toggle('product_interests', p)} />
                        <SelectField label="Where do you sell or work with clients?" value={v.ecommerce_platform} options={PLATFORMS} error={show('ecommerce_platform')}
                            onChange={(x) => set({ ecommerce_platform: x })} />
                        <SelectField label="Current or expected orders per month" value={v.volume_band} options={VOLUMES} error={show('volume_band')}
                            onChange={(x) => set({ volume_band: x })} />
                    </div>
                ) : null}

                {draft.step === 1 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField label="First name" value={v.first_name} auto="given-name" max={100} error={show('first_name')} onChange={(x) => set({ first_name: x })} />
                        <TextField label="Last name" value={v.last_name} auto="family-name" max={100} error={show('last_name')} onChange={(x) => set({ last_name: x })} />
                        <div className="sm:col-span-2"><TextField label="Email address" type="email" value={v.email} auto="email" max={254} error={show('email')} onChange={(x) => set({ email: x })} /></div>
                        <div className="sm:col-span-2"><TextField label="Brand or studio name" value={v.company_name} auto="organization" max={160} placeholder="Your name is fine if you’re just getting started"
                            error={show('company_name')} onChange={(x) => set({ company_name: x })} /></div>
                        <TextField label="Website" optional value={v.website} auto="url" max={500} placeholder="yourbrand.com" hint="Just your domain is fine." error={show('website')} onChange={(x) => set({ website: x })} />
                        <TextField label="Phone" type="tel" value={v.phone} auto="tel" max={50} placeholder="+1 555 555 0100" hint="So your specialist can reach you." error={show('phone')} onChange={(x) => set({ phone: x })} />
                        <div className="sm:col-span-2"><NewsOptIn checked={v.marketing_consent} onChange={(on) => set({ marketing_consent: on })} /></div>
                    </div>
                ) : null}

                {showAccount ? (
                    <div className="flex flex-col gap-y-3">
                        <AccountStep values={v} onCreate={createAccount} onEditDetails={() => { setAccountOpen(false); setDraft((d) => ({ ...d, step: 1 })); }} />
                        <button type="button" disabled={!!busy} onClick={book} className="self-center text-sm font-medium text-zinc-600 hover:text-zinc-900 disabled:opacity-60 dark:text-zinc-400 dark:hover:text-zinc-100">
                            {busy === 'book' ? 'One moment…' : 'Rather talk to us first? Book a call'}
                        </button>
                    </div>
                ) : null}

                {draft.step === 2 && !showAccount ? (
                    <div className={`grid grid-cols-1 gap-3 ${order.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                        {order.map((door, i) => (
                            <button key={door} type="button" disabled={!!busy} onClick={() => { if (door === 'book') { book(); return; } setError(null); setAccountOpen(true); }}
                                className={`group flex flex-col items-start gap-y-1.5 rounded-2xl border p-4 text-left transition hover:border-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:opacity-60 ${i === 0 ? 'border-emerald-600/60 bg-emerald-600/5' : 'border-surface-border'}`}>
                                {i === 0 && order.length > 1 ? <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Recommended for you</span> : null}
                                <strong className="text-base">{DOORS[door].title}</strong>
                                <span className="text-sm text-zinc-600 dark:text-zinc-400">{DOORS[door].copy}</span>
                                <em className="mt-1 inline-flex items-center gap-1 text-sm font-semibold not-italic text-emerald-700 dark:text-emerald-400">
                                    {busy === door ? 'One moment…' : DOORS[door].cta} <ArrowRightIcon className="h-4 w-4" />
                                </em>
                            </button>
                        ))}
                    </div>
                ) : null}

                <InlineError message={error} />

                <div className="flex items-center justify-between gap-3">
                    {draft.step > 0 ? <button type="button" onClick={back} className="text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100">← Back</button> : <span />}
                    {draft.step < 2 ? (
                        <button type="button" onClick={next} className="inline-flex h-11 items-center gap-1 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2">
                            Continue <ArrowRightIcon className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>

                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    By continuing, you agree Wallmates may contact you about your account or call. Your answers tailor your setup.{' '}
                    <a href="https://wallmates.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy policy</a>
                </p>
            </div>
        </BookingShell>
    );
};

// #region ChoiceGroup
const ChoiceGroup = <T extends string>({ title, help, options, picked, onToggle, error }: {
    title: string; help: string; options: [T, string][]; picked: T[]; onToggle: (value: T) => void; error?: string;
}): React.ReactElement => (
    <fieldset>
        <legend className="text-sm font-medium">{title}</legend>
        <p className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">{help}</p>
        <div className="flex flex-wrap gap-2">
            {options.map(([value, label]) => {
                const on = picked.includes(value);
                return (
                    <button key={value} type="button" role="checkbox" aria-checked={on} onClick={() => onToggle(value)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${on ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-surface-border hover:border-emerald-600'}`}>
                        {on ? <CheckIcon className="h-4 w-4" strokeWidth={2.5} /> : null}{label}
                    </button>
                );
            })}
        </div>
        {error ? <p role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p> : null}
    </fieldset>
);

// #region TextField
const TextField: React.FunctionComponent<{
    label: string; value: string; onChange: (v: string) => void; auto: string; max: number;
    type?: string; optional?: boolean; placeholder?: string; hint?: string; error?: string;
}> = ({ label, value, onChange, auto, max, type = 'text', optional, placeholder, hint, error }) => (
    <label className="flex flex-col gap-y-1 text-sm font-medium">
        <span>{label}{optional ? <span className="font-normal text-zinc-500"> (optional)</span> : null}</span>
        <input type={type} value={value} autoComplete={auto} maxLength={max} placeholder={placeholder} aria-invalid={!!error}
            onChange={(ev) => onChange(ev.target.value)} className={inputCls(!!error)} />
        {hint && !error ? <span className="text-xs font-normal text-zinc-500">{hint}</span> : null}
        {error ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">{error}</span> : null}
    </label>
);

// #region SelectField
const SelectField: React.FunctionComponent<{ label: string; value: string; options: readonly string[]; onChange: (v: string) => void; error?: string }> = ({ label, value, options, onChange, error }) => (
    <label className="flex flex-col gap-y-1 text-sm font-medium">
        <span>{label}</span>
        <select value={value} aria-invalid={!!error} onChange={(ev) => onChange(ev.target.value)} className={inputCls(!!error)}>
            <option value="">Choose an option</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {error ? <span role="alert" className="text-xs font-normal text-red-600 dark:text-red-400">{error}</span> : null}
    </label>
);

export default GetStarted;
